if (!customElements.get("sub-widget")) {
  customElements.define(
    "sub-widget",
    class SubWidget extends HTMLElement {
      constructor() {
        super();
        this.unsubscribeVariantChange = null;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        if (typeof this.unsubscribeVariantChange === "function") {
          this.unsubscribeVariantChange();
        }

        this.unsubscribeVariantChange = null;
      }

      init() {
        this.cacheElements();
        this.addPlanListeners();
        this.addVariantChangeListener();
        this.setInitialPlan();
      }

      reinit() {
        this.cacheElements();
        this.addPlanListeners();
        this.setInitialPlan(true);
      }

      cacheElements() {
        /*
         * In popup mode the hidden selling_plan input is rendered outside this
         * element (and outside the <template>), so that the product form has
         * the right plan before the sheet is ever opened. `data-selling-plan-input`
         * carries its id in that case.
         */
        this.sellingPlanInput =
          this.querySelector('input[name="selling_plan"]') ||
          (this.dataset.sellingPlanInput
            ? document.getElementById(this.dataset.sellingPlanInput)
            : null);

        this.allPlans = Array.from(
          this.querySelectorAll(
            ".wt-subscription-widget__plans[data-variant-id]"
          )
        );
      }

      getProductForm() {
        if (this.sellingPlanInput) {
          const formId = this.sellingPlanInput.getAttribute("form");

          if (formId) {
            const formById = document.getElementById(formId);
            if (formById) return formById;
          }

          if (this.sellingPlanInput.form) {
            return this.sellingPlanInput.form;
          }

          const closestForm = this.sellingPlanInput.closest("form");
          if (closestForm) return closestForm;
        }

        /*
         * Featured-product sections can render the widget before the browser
         * has associated the external hidden input with its product form.
         * Search only inside this section so multiple product forms on a page
         * do not interfere with one another.
         */
        const sectionWrapper = this.closest("[data-section-id]");

        return (
          sectionWrapper?.querySelector(
            'product-form form[action*="/cart/add"]'
          ) ||
          sectionWrapper?.querySelector(
            'form[id^="product-form-"][action*="/cart/add"]'
          ) ||
          sectionWrapper?.querySelector('form[action*="/cart/add"]') ||
          null
        );
      }

      getVariantInput() {
        return (
          this.getProductForm()?.querySelector('input[name="id"]') || null
        );
      }

      addPlanListeners() {
        this.allPlans.forEach((plan) => {
          if (plan.dataset.subscriptionClickBound === "true") return;

          plan.dataset.subscriptionClickBound = "true";

          plan.addEventListener("click", (event) => {
            /*
             * Allow links and the nested Future Gifts accordion to work
             * without re-triggering the purchase-option handler.
             */
            if (
              event.target.closest(
                "a, button, input, select, textarea, summary, details"
              )
            ) {
              return;
            }

            this.handlePlanClick(plan, true, true);
          });

          plan.addEventListener("keydown", (event) => {
            if (event.key !== "Enter" && event.key !== " ") return;

            event.preventDefault();
            this.handlePlanClick(plan, true, true);
          });
        });
      }

      addVariantChangeListener() {
        if (
          this.unsubscribeVariantChange ||
          typeof subscribe !== "function" ||
          typeof PUB_SUB_EVENTS === "undefined"
        ) {
          return;
        }

        this.unsubscribeVariantChange = subscribe(
          PUB_SUB_EVENTS.variantChange,
          (event) => {
            const wrapper = this.closest("[data-section-id]");
            const sectionId = wrapper?.dataset?.sectionId;
            const originalSectionId = wrapper?.dataset?.originalSectionId;

            if (
              sectionId &&
              event?.data?.sectionId !== sectionId &&
              event?.data?.sectionId !== originalSectionId
            ) {
              return;
            }

            const parsedContainer = event?.data?.html?.querySelector(
              ".wt-subscription-widget__container"
            );

            const currentContainer = this.querySelector(
              ".wt-subscription-widget__container"
            );

            if (!parsedContainer || !currentContainer) return;

            currentContainer.innerHTML = parsedContainer.innerHTML;

            if (originalSectionId) {
              const hiddenInput = this.querySelector(
                'input[name="selling_plan"]'
              );

              if (hiddenInput) {
                hiddenInput.setAttribute(
                  "form",
                  `product-form-${sectionId}`
                );
              }
            }

            this.reinit();
          }
        );
      }

      setInitialPlan(emitEvent = false) {
        const selectedPlan =
          this.querySelector(
            ".wt-subscription-widget__plans--selected:not(.wt-subscription-widget__plans--disabled)"
          ) ||
          this.allPlans.find(
            (plan) =>
              !plan.classList.contains(
                "wt-subscription-widget__plans--disabled"
              ) && plan.dataset.available !== "false"
          ) ||
          this.allPlans[0];

        if (selectedPlan) {
          this.handlePlanClick(selectedPlan, false, emitEvent);
        }
      }

      setVariantId(variantId) {
        if (!variantId) return;

        const variantInput = this.getVariantInput();

        if (!variantInput) {
          console.warn(
            "Subscription widget: product variant input was not found."
          );
          return;
        }

        const value = String(variantId);

        /*
         * Do not dispatch the theme's variant-change event here. In this
         * theme, that event rerenders the product section and can reset the
         * selected subscription term. Updating the form value is sufficient
         * for Add to Cart and dynamic checkout submission.
         */
        variantInput.value = value;
        variantInput.setAttribute("value", value);

        const sectionId = this.closest("[data-section-id]")?.dataset.sectionId;
        document.querySelectorAll("variant-options").forEach((picker) => {
          if (sectionId && picker.dataset.section === sectionId &&
              typeof picker.selectPurchaseVariant === "function") {
            picker.selectPurchaseVariant(value);
          }
        });
      }

      setSellingPlanValue(sellingPlanId) {
        if (!this.sellingPlanInput) return;

        if (sellingPlanId) {
          const value = String(sellingPlanId);
          this.sellingPlanInput.value = value;
          this.sellingPlanInput.setAttribute("value", value);
        } else {
          this.sellingPlanInput.value = "";
          this.sellingPlanInput.removeAttribute("value");
        }
      }

      setProductFormAvailability(available) {
        const form = this.getProductForm();
        const submitButton = form?.querySelector(
          '[type="submit"][name="add"]'
        );

        if (!submitButton) return;

        submitButton.disabled = !available;
        submitButton.setAttribute(
          "aria-disabled",
          available ? "false" : "true"
        );
      }

      setExpandedState(selectedPlan) {
        this.allPlans.forEach((plan) => {
          const isSelected = plan === selectedPlan;
          const canExpand =
            plan.dataset.purchaseOption === "subscription" &&
            Boolean(
              plan.querySelector(
                ".wt-subscription-widget__plan-content"
              )
            );

          const isExpanded = isSelected && canExpand;

          plan.classList.toggle(
            "wt-subscription-widget__plans--expanded",
            isExpanded
          );

          plan.setAttribute(
            "aria-expanded",
            isExpanded ? "true" : "false"
          );
        });
      }

      handlePlanClick(plan, updateUrl = true, emitEvent = true) {
        if (
          !plan ||
          plan.classList.contains(
            "wt-subscription-widget__plans--disabled"
          ) ||
          plan.dataset.available === "false"
        ) {
          return;
        }

        this.allPlans.forEach((item) => {
          const isSelected = item === plan;

          item.classList.toggle(
            "wt-subscription-widget__plans--selected",
            isSelected
          );

          item.setAttribute("aria-checked", isSelected ? "true" : "false");
          item.setAttribute("tabindex", isSelected ? "0" : "-1");
        });

        this.setExpandedState(plan);

        const variantId = plan.dataset.variantId || "";
        const sellingPlanId = plan.dataset.sellingPlanId || "";

        this.setVariantId(variantId);
        this.setSellingPlanValue(sellingPlanId);
        this.setProductFormAvailability(
          plan.dataset.available !== "false"
        );

        const priceData = this.getPriceDataFromPlan(plan);

        this.updatePriceBlock(priceData);
        this.updateStickyPriceBlock(priceData);

        if (updateUrl) {
          this.updateURLParamsIfEnabled(variantId, sellingPlanId);
        }

        if (emitEvent) {
          this.dispatchSelectionEvent(plan, priceData);
        }
      }

      getPriceDataFromPlan(plan) {
        return {
          price:
            plan.dataset.priceFormatted ||
            plan.dataset.price ||
            "",
          compareAtPrice:
            plan.dataset.compareAtPriceFormatted ||
            plan.dataset.compareAtPrice ||
            "",
          priceRaw: Number(plan.dataset.priceRaw || 0),
          compareAtPriceRaw: Number(
            plan.dataset.compareAtPriceRaw || 0
          ),
        };
      }

      dispatchSelectionEvent(plan, priceData) {
        const detail = {
          purchaseOption:
            plan.dataset.purchaseOption ||
            (plan.dataset.sellingPlanId ? "subscription" : "onetime"),
          term: plan.dataset.term || "",
          variantId: plan.dataset.variantId || "",
          sellingPlanId: plan.dataset.sellingPlanId || "",
          available: plan.dataset.available !== "false",
          label:
            plan.dataset.optionLabel ||
            plan.querySelector(
              ".wt-subscription-widget__plan-header__title-text"
            )?.textContent?.trim() ||
            "",
          summaryPrice:
            plan.dataset.summaryPrice || priceData.price || "",
          summarySuffix:
            plan.dataset.summarySuffix ||
            (plan.dataset.purchaseOption === "subscription" ? "/ mo" : ""),
          savePercent: Number(plan.dataset.savePercent || 0),
          ...priceData,
        };

        this.dispatchEvent(
          new CustomEvent("subscription:change", {
            bubbles: true,
            detail,
          })
        );
      }

      updatePriceBlock(priceData) {
        const sectionId = this.closest("[data-section-id]")?.dataset
          ?.sectionId;

        if (!sectionId) return;

        const priceBlock = document.getElementById(`price-${sectionId}`);

        if (priceBlock) {
          this.updatePriceContainer(priceBlock, priceData);
        }
      }

      updateStickyPriceBlock(priceData) {
        const sectionId = this.closest("[data-section-id]")?.dataset
          ?.sectionId;

        if (!sectionId) return;

        const stickyPriceBlock = document.getElementById(
          `sticky-price-${sectionId}`
        );

        if (stickyPriceBlock) {
          this.updatePriceContainer(stickyPriceBlock, priceData);
        }
      }

      updatePriceContainer(
        block,
        { price, compareAtPrice, priceRaw, compareAtPriceRaw }
      ) {
        const priceContainer = block.querySelector(".price") || block;
        const normalizedPriceRaw = Number(priceRaw || 0);
        const normalizedCompareRaw = Number(compareAtPriceRaw || 0);

        const isOnSale =
          normalizedCompareRaw > 0 &&
          normalizedCompareRaw > normalizedPriceRaw;

        priceContainer.classList.toggle("price--on-sale", isOnSale);

        const regularWrapper = priceContainer.querySelector(
          ".price__regular"
        );
        const saleWrapper = priceContainer.querySelector(".price__sale");

        if (regularWrapper) {
          regularWrapper.hidden = isOnSale;
          regularWrapper.style.display = isOnSale ? "none" : "";
          regularWrapper.setAttribute(
            "aria-hidden",
            isOnSale ? "true" : "false"
          );
        }

        if (saleWrapper) {
          saleWrapper.hidden = !isOnSale;
          saleWrapper.style.display = isOnSale ? "" : "none";
          saleWrapper.setAttribute(
            "aria-hidden",
            isOnSale ? "false" : "true"
          );
          saleWrapper.classList.toggle("visible", isOnSale);
          saleWrapper.classList.toggle("visible-main-product", isOnSale);
        }

        this.setPriceText(regularWrapper, price);
        this.setPriceText(saleWrapper, price);

        if (!regularWrapper && !saleWrapper) {
          this.setPriceText(priceContainer, price);
        }

        const compareElements = priceContainer.querySelectorAll(
          ".wt-product__price__compare, " +
            ".price-item--compare, " +
            "s.price-item, " +
            ".price__compare"
        );

        compareElements.forEach((element) => {
          element.innerHTML = isOnSale ? compareAtPrice || "" : "";
          element.hidden = !isOnSale;
          element.style.display = isOnSale ? "" : "none";
          element.classList.toggle("hidden", !isOnSale);
        });

        let percentageElement = priceContainer.querySelector(
          ".price-item--percent"
        );

        if (isOnSale && normalizedCompareRaw > 0) {
          const percentage = Math.round(
            ((normalizedCompareRaw - normalizedPriceRaw) /
              normalizedCompareRaw) *
              100
          );

          if (!percentageElement) {
            const detailsWrapper = priceContainer.querySelector(
              ".price__sale__details-wrapper"
            );

            if (detailsWrapper) {
              percentageElement = document.createElement("span");
              percentageElement.className = "price-item--percent";
              detailsWrapper.appendChild(percentageElement);
            }
          }

          if (percentageElement) {
            percentageElement.textContent = `-${percentage}%`;
            percentageElement.hidden = false;
            percentageElement.style.display = "";
          }
        } else if (percentageElement) {
          percentageElement.remove();
        }
      }

      setPriceText(wrapper, price) {
        if (!wrapper || !price) return;

        const candidates = wrapper.querySelectorAll(
          ".wt-product__price__final, " +
            ".price-item--sale, " +
            ".price-item--regular, " +
            ".money, " +
            "[data-product-price]"
        );

        let updated = false;

        candidates.forEach((element) => {
          if (
            element.matches(
              ".wt-product__price__compare, " +
                ".price-item--compare, " +
                ".price-item--percent"
            ) ||
            element.closest("s")
          ) {
            return;
          }

          element.innerHTML = price;
          updated = true;
        });

        if (!updated && wrapper.children.length === 0) {
          wrapper.innerHTML = price;
        }
      }

      updateURLParamsIfEnabled(variantId, sellingPlanId) {
        const wrapper = this.closest(
          "[data-update-url-on-plan-change]"
        );

        if (wrapper?.dataset?.updateUrlOnPlanChange === "false") {
          return;
        }

        const url = new URL(window.location.href);

        if (variantId) {
          url.searchParams.set("variant", variantId);
        }

        if (sellingPlanId) {
          url.searchParams.set("selling_plan", sellingPlanId);
        } else {
          url.searchParams.delete("selling_plan");
        }

        window.history.replaceState(
          {},
          "",
          `${url.pathname}${url.search}${url.hash}`
        );
      }
    }
  );
}
